import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { NetworkIcon, CableIcon, DatabaseIcon, MailIcon, VideoIcon, SmartphoneIcon, ShieldCheckIcon, BlocksIcon, ArrowRightIcon, LockIcon } from 'lucide-react';
const services = [{
  title: 'Networking',
  description: 'Secure, high-performance networking solutions to boost connectivity and ensure reliable business communication.',
  icon: NetworkIcon,
  color: 'text-blue-500',
  bg: 'bg-blue-50'
}, {
  title: 'Cat 5 / Cat 6 Cabling',
  description: 'Expert installation of Cat 5 and Cat 6 cabling, ensuring fast and dependable network performance.',
  icon: CableIcon,
  color: 'text-teal-500',
  bg: 'bg-teal-50'
}, {
  title: 'Data Backup and Recovery',
  description: 'Securely back up and quickly restore your critical data, minimizing downtime and ensuring continuity.',
  icon: DatabaseIcon,
  color: 'text-green-500',
  bg: 'bg-green-50'
}, {
  title: 'Microsoft Office 365',
  description: 'Complete management of Microsoft Office 365 for improved productivity and seamless collaboration.',
  icon: MailIcon,
  color: 'text-orange-500',
  bg: 'bg-orange-50'
}, {
  title: 'Multi-Site Video Security System',
  description: 'Centralized video security for monitoring and managing multiple locations with ease.',
  icon: VideoIcon,
  color: 'text-red-500',
  bg: 'bg-red-50'
}, {
  title: 'Smart Device Control',
  description: 'Remote management of smart devices to optimize operations, enhance security, and provide greater control over your business environment.',
  icon: SmartphoneIcon,
  color: 'text-violet-500',
  bg: 'bg-violet-50'
}, {
  title: 'Surveillance Security Systems',
  description: 'Advanced security systems installation to safeguard your business premises effectively.',
  icon: ShieldCheckIcon,
  color: 'text-amber-500',
  bg: 'bg-amber-50'
}, {
  title: 'App Integration',
  description: 'Efficient application integration to streamline business processes and enhance functionality.',
  icon: BlocksIcon,
  color: 'text-pink-500',
  bg: 'bg-pink-50'
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4
    }
  }
};
export function NetworkSecurityPage({
  onNavigate
}: {
  onNavigate: (page: string) => void;
}) {
  return <div data-id="element-789">
      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-24 overflow-hidden bg-slate-900" data-id="element-790">
        <div className="absolute inset-0 bg-grid-pattern opacity-20" data-id="element-791"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/90" data-id="element-792"></div>
        <div className="absolute top-0 right-1/4 -translate-y-1/4 w-[600px] h-[600px] bg-red-500/10 rounded-full blur-[120px] pointer-events-none" data-id="element-793"></div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-794">
          <div className="max-w-3xl" data-id="element-795">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-796">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-white/10 border border-white/20 text-white text-sm font-medium mb-6 backdrop-blur-sm" data-id="element-797">
                <span className="flex h-2 w-2 rounded-full bg-red-400 mr-2" data-id="element-798"></span>
                Comprehensive IT &amp; Security Solutions
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight mb-6 tracking-tight" data-id="element-799">
                Network Security &<br className="hidden md:block" data-id="element-800" />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 to-orange-400" data-id="element-801">
                  Management Solutions
                </span>
              </h1>
              <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-8" data-id="element-802">
                We understand the critical importance of safeguarding your
                business's digital infrastructure. Our network security
                management services are designed to protect your data, ensure
                seamless connectivity, and provide peace of mind.
              </p>
              <button onClick={() => onNavigate('home')} className="bg-primary hover:bg-primary-dark text-white px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg inline-flex items-center" data-id="element-803">
                Get a Free Security Assessment
                <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-804" />
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-white" data-id="element-805">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-806">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-807">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-808">
              Our Services
            </h2>
            <p className="text-lg text-slate-600" data-id="element-809">
              With a focus on cutting-edge technology and expert support, we
              deliver tailored solutions to meet the unique needs of your
              organization.
            </p>
          </div>

          <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-60px'
        }} data-id="element-810">
            {services.map((service, i) => {
            const Icon = service.icon;
            return <motion.div key={i} variants={itemVariants} className="bg-slate-50 rounded-2xl p-6 border border-slate-100 flex flex-col" data-id="element-811">
                  <div className={`w-12 h-12 ${service.bg} rounded-xl flex items-center justify-center mb-5`} data-id="element-812">
                    <Icon className={`w-6 h-6 ${service.color}`} data-id="element-813" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-814">
                    {service.title}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed flex-grow" data-id="element-815">
                    {service.description}
                  </p>
                </motion.div>;
          })}
          </motion.div>
        </div>
      </section>

      {/* About Our Approach */}
      <section className="py-20 bg-slate-50" data-id="element-816">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-817">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-818">
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="relative" data-id="element-819">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl" data-id="element-820">
                <img src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=800&h=600&fit=crop" alt="Network security operations" className="w-full h-auto object-cover" data-id="element-821" />
                <div className="absolute inset-0 bg-gradient-to-tr from-slate-900/40 to-transparent" data-id="element-822"></div>
              </div>
              {/* Floating badge */}
              <div className="absolute -bottom-6 -right-4 md:right-8 bg-white rounded-2xl shadow-xl p-5 border border-slate-100" data-id="element-823">
                <div className="flex items-center gap-3" data-id="element-824">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center" data-id="element-825">
                    <LockIcon className="w-6 h-6 text-green-600" data-id="element-826" />
                  </div>
                  <div data-id="element-827">
                    <div className="text-sm font-bold text-slate-900" data-id="element-828">
                      24/7 Monitoring
                    </div>
                    <div className="text-xs text-slate-500" data-id="element-829">
                      Always protected
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} data-id="element-830">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6" data-id="element-831">
                Protecting Your Digital Infrastructure
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-6" data-id="element-832">
                We understand the critical importance of safeguarding your
                business's digital infrastructure. Network security management
                services are designed to protect your data, ensure seamless
                connectivity, and provide peace of mind.
              </p>
              <p className="text-lg text-slate-600 leading-relaxed mb-8" data-id="element-833">
                With a focus on cutting-edge technology and expert support, we
                deliver tailored solutions to meet the unique needs of your
                organization.
              </p>
              <div className="grid grid-cols-2 gap-4" data-id="element-834">
                {['Threat Detection', 'Data Encryption', 'Access Control', 'Compliance'].map((item, i) => <div key={i} className="flex items-center gap-2" data-id="element-835">
                    <div className="w-6 h-6 bg-primary/10 rounded-md flex items-center justify-center flex-shrink-0" data-id="element-836">
                      <ShieldCheckIcon className="w-3.5 h-3.5 text-primary" data-id="element-837" />
                    </div>
                    <span className="text-slate-800 font-medium text-sm" data-id="element-838">
                      {item}
                    </span>
                  </div>)}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary relative overflow-hidden py-20" data-id="element-839">
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-840">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-white opacity-5 rounded-full blur-3xl" data-id="element-841"></div>
          <div className="absolute -bottom-24 -left-24 w-96 h-96 bg-white opacity-10 rounded-full blur-3xl" data-id="element-842"></div>
        </div>

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10" data-id="element-843">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-id="element-844">
            Secure Your Business Today!
          </h2>
          <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto" data-id="element-845">
            We're committed to providing top-tier network security management
            and IT services to help your business thrive in today's digital
            landscape. Let us be your trusted partner in safeguarding your
            network and ensuring uninterrupted business operations.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-846">
            <button onClick={() => onNavigate('home')} className="w-full sm:w-auto bg-white text-primary hover:bg-slate-50 px-8 py-4 rounded-full font-bold text-lg transition-colors shadow-lg inline-flex items-center justify-center" data-id="element-847">
              Get Your Free Assessment
              <ArrowRightIcon className="w-5 h-5 ml-2" data-id="element-848" />
            </button>
            <a href="tel:+4105255284" className="w-full sm:w-auto bg-transparent border-2 border-white text-white hover:bg-white/10 px-8 py-4 rounded-full font-bold text-lg transition-colors" data-id="element-849">
              Call Us: +410-525-5284
            </a>
          </div>
        </div>
      </section>
    </div>;
}